import React, { useState, FormEvent, useEffect } from "react";
import { Container, Form, Button, Card } from "react-bootstrap";
import {
  CTRIndustries,
  CTRResult,
  CTRIndustry,
  AdType,
  Benchmarks,
  Recommendations,
} from "./types";
import { FaChartBar, FaLightbulb } from "react-icons/fa";
// import "./CtrCalculator.css";
import { Box } from "./CtrCalculatorStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const CTR_INDUSTRY_DATA: CTRIndustries = {
  ecommerce: {
    name: "E-commerce",
    benchmarks: {
      displayAds: { min: 0.5, max: 1.5 },
      searchAds: { min: 2.0, max: 3.0 },
      socialAds: { min: 1.0, max: 2.0 },
      videoAds: { min: 1.2, max: 2.5 },
      emailAds: { min: 2.5, max: 4.0 },
    },
    recommendations: {
      displayAds: [
        "Use high-quality product images",
        "Implement dynamic retargeting",
        "Test different ad sizes and placements",
        "Include compelling price points",
        "Add urgency with limited-time offers",
        "Test animated banners or GIFs to grab attention",
        "Optimize ad frequency to avoid banner fatigue",
        "Use contrasting colors to highlight CTAs",
        "Geo-target audiences for local relevance",
      ],
      searchAds: [
        "Use specific product names and SKUs",
        "Include pricing in ad copy",
        "Add promotional codes",
        "Optimize for mobile search",
        "Use negative keywords effectively",
        "Use ad extensions (sitelinks, callouts, snippets)",
        "Test different keyword match types",
        "Include strong CTAs in headlines",
        "Audit and pause low-performing keywords",
      ],
      socialAds: [
        "Leverage user-generated content",
        "Create carousel ads showcasing products",
        "Use social proof in ad copy",
        "Target lookalike audiences",
        "Test different ad formats",
        "Run A/B tests on creatives",
        "Use interactive formats like polls or quizzes",
        "Collaborate with influencers",
        "Optimize for mobile-first consumption",
      ],
      videoAds: [
        "Show products in action",
        "Keep videos under 30 seconds",
        "Add captions for silent viewing",
        "Include clear CTAs",
        "Showcase product benefits quickly",
        "Use storytelling to build emotional connection",
        "Place CTA in the first 10 seconds",
        "Test vertical vs. horizontal video formats",
        "Optimize thumbnails for higher CTR",
      ],
      emailAds: [
        "Segment your email list",
        "Personalize subject lines",
        "Test different send times",
        "Use clear CTAs",
        "Include product recommendations",
        "Optimize for mobile-first reading",
        "Use emojis in subject lines (if suitable)",
        "Test preheader text for better open rates",
        "Clean inactive subscribers to improve deliverability",
      ],
    },
  },
  technology: {
    name: "Technology",
    benchmarks: {
      displayAds: { min: 0.4, max: 1.2 },
      searchAds: { min: 1.8, max: 2.8 },
      socialAds: { min: 0.8, max: 1.8 },
      videoAds: { min: 1.0, max: 2.2 },
      emailAds: { min: 2.0, max: 3.5 },
    },
    recommendations: {
      displayAds: [
        "Highlight technical specifications",
        "Use clean, modern designs",
        "Include product comparisons",
        "Showcase innovation features",
        "Target tech-savvy audiences",
        "Test animated banners or GIFs to grab attention",
        "Optimize ad frequency to avoid banner fatigue",
        "Use contrasting colors to highlight CTAs",
        "Geo-target audiences for local relevance",
      ],
      searchAds: [
        "Use technical keywords",
        "Include specs in ad copy",
        "Target solution-seekers",
        "Highlight compatibility",
        "Focus on benefits over features",
        "Use ad extensions (sitelinks, callouts, snippets)",
        "Test different keyword match types",
        "Include strong CTAs in headlines",
        "Audit and pause low-performing keywords",
      ],
      socialAds: [
        "Share tech tips and tricks",
        "Use video demonstrations",
        "Engage with tech communities",
        "Showcase user success stories",
        "Highlight industry awards",
        "Run A/B tests on creatives",
        "Use interactive formats like polls or quizzes",
        "Collaborate with influencers",
        "Optimize for mobile-first consumption",
      ],
      videoAds: [
        "Demonstrate product features",
        "Include technical tutorials",
        "Show real-world applications",
        "Compare with competitors",
        "Include expert testimonials",
        "Use storytelling to build emotional connection",
        "Place CTA in the first 10 seconds",
        "Test vertical vs. horizontal video formats",
        "Optimize thumbnails for higher CTR",
      ],
      emailAds: [
        "Segment by tech expertise",
        "Include tech specs",
        "Offer technical support",
        "Share product updates",
        "Provide setup guides",
        "Optimize for mobile-first reading",
        "Use emojis in subject lines (if suitable)",
        "Test preheader text for better open rates",
        "Clean inactive subscribers to improve deliverability",
      ],
    },
  },
  finance: {
    name: "Finance",
    benchmarks: {
      displayAds: { min: 0.3, max: 1.0 },
      searchAds: { min: 1.5, max: 2.5 },
      socialAds: { min: 0.6, max: 1.5 },
      videoAds: { min: 0.8, max: 2.0 },
      emailAds: { min: 2.0, max: 3.0 },
    },
    recommendations: {
      displayAds: [
        "Emphasize security features",
        "Use professional imagery",
        "Include trust indicators",
        "Show financial benefits",
        "Target high-value customers",
        "Test animated banners or GIFs to grab attention",
        "Optimize ad frequency to avoid banner fatigue",
        "Use contrasting colors to highlight CTAs",
        "Geo-target audiences for local relevance",
      ],
      searchAds: [
        "Focus on financial terms",
        "Include ROI metrics",
        "Target financial keywords",
        "Highlight security features",
        "Use professional language",
        "Use ad extensions (sitelinks, callouts, snippets)",
        "Test different keyword match types",
        "Include strong CTAs in headlines",
        "Audit and pause low-performing keywords",
      ],
      socialAds: [
        "Share financial tips",
        "Use data visualizations",
        "Target financial professionals",
        "Include market insights",
        "Showcase success stories",
        "Run A/B tests on creatives",
        "Use interactive formats like polls or quizzes",
        "Collaborate with influencers",
        "Optimize for mobile-first consumption",
      ],
      videoAds: [
        "Explain financial concepts",
        "Show platform security",
        "Include expert opinions",
        "Demonstrate ROI",
        "Use professional presenters",
        "Use storytelling to build emotional connection",
        "Place CTA in the first 10 seconds",
        "Test vertical vs. horizontal video formats",
        "Optimize thumbnails for higher CTR",
      ],
      emailAds: [
        "Segment by portfolio size",
        "Include market updates",
        "Offer financial advice",
        "Share investment tips",
        "Highlight security features",
        "Optimize for mobile-first reading",
        "Use emojis in subject lines (if suitable)",
        "Test preheader text for better open rates",
        "Clean inactive subscribers to improve deliverability",
      ],
    },
  },
  healthcare: {
    name: "Healthcare",
    benchmarks: {
      displayAds: { min: 0.4, max: 1.2 },
      searchAds: { min: 1.7, max: 2.7 },
      socialAds: { min: 0.7, max: 1.7 },
      videoAds: { min: 0.9, max: 2.1 },
      emailAds: { min: 2.2, max: 3.5 },
    },
    recommendations: {
      displayAds: [
        "Use caring imagery",
        "Include health benefits",
        "Show professional staff",
        "Highlight certifications",
        "Focus on patient care",
        "Test animated banners or GIFs to grab attention",
        "Optimize ad frequency to avoid banner fatigue",
        "Use contrasting colors to highlight CTAs",
        "Geo-target audiences for local relevance",
      ],
      searchAds: [
        "Target health conditions",
        "Include credentials",
        "Use medical terms",
        "Highlight expertise",
        "Focus on patient benefits",
        "Use ad extensions (sitelinks, callouts, snippets)",
        "Test different keyword match types",
        "Include strong CTAs in headlines",
        "Audit and pause low-performing keywords",
      ],
      socialAds: [
        "Share health tips",
        "Show success stories",
        "Include expert advice",
        "Use educational content",
        "Build trust through expertise",
        "Run A/B tests on creatives",
        "Use interactive formats like polls or quizzes",
        "Collaborate with influencers",
        "Optimize for mobile-first consumption",
      ],
      videoAds: [
        "Show patient testimonials",
        "Demonstrate procedures",
        "Include expert interviews",
        "Show facility tours",
        "Highlight technology",
        "Use storytelling to build emotional connection",
        "Place CTA in the first 10 seconds",
        "Test vertical vs. horizontal video formats",
        "Optimize thumbnails for higher CTR",
      ],
      emailAds: [
        "Segment by health interests",
        "Include wellness tips",
        "Offer health resources",
        "Share medical news",
        "Provide appointment reminders",
        "Optimize for mobile-first reading",
        "Use emojis in subject lines (if suitable)",
        "Test preheader text for better open rates",
        "Clean inactive subscribers to improve deliverability",
      ],
    },
  },
  education: {
    name: "Education",
    benchmarks: {
      displayAds: { min: 0.5, max: 1.3 },
      searchAds: { min: 1.9, max: 2.9 },
      socialAds: { min: 0.9, max: 1.9 },
      videoAds: { min: 1.1, max: 2.3 },
      emailAds: { min: 2.3, max: 3.8 },
    },
    recommendations: {
      displayAds: [
        "Show student success stories",
        "Include program details",
        "Highlight faculty expertise",
        "Show campus life",
        "Feature alumni achievements",
        "Test animated banners or GIFs to grab attention",
        "Optimize ad frequency to avoid banner fatigue",
        "Use contrasting colors to highlight CTAs",
        "Geo-target audiences for local relevance",
      ],
      searchAds: [
        "Target specific programs",
        "Include enrollment dates",
        "Highlight accreditations",
        "Show career outcomes",
        "Use education keywords",
        "Use ad extensions (sitelinks, callouts, snippets)",
        "Test different keyword match types",
        "Include strong CTAs in headlines",
        "Audit and pause low-performing keywords",
      ],
      socialAds: [
        "Share student experiences",
        "Show campus events",
        "Include faculty profiles",
        "Highlight research",
        "Feature student work",
        "Run A/B tests on creatives",
        "Use interactive formats like polls or quizzes",
        "Collaborate with influencers",
        "Optimize for mobile-first consumption",
      ],
      videoAds: [
        "Show campus tours",
        "Include student testimonials",
        "Demonstrate facilities",
        "Show class interactions",
        "Feature program highlights",
        "Use storytelling to build emotional connection",
        "Place CTA in the first 10 seconds",
        "Test vertical vs. horizontal video formats",
        "Optimize thumbnails for higher CTR",
      ],
      emailAds: [
        "Segment by program interest",
        "Include application deadlines",
        "Share student stories",
        "Offer program information",
        "Highlight career outcomes",
        "Optimize for mobile-first reading",
        "Use emojis in subject lines (if suitable)",
        "Test preheader text for better open rates",
        "Clean inactive subscribers to improve deliverability",
      ],
    },
  },
};

const CtrCalculator: React.FC = () => {
  const [impressions, setImpressions] = useState<number | "">("");
  const [clicks, setClicks] = useState<number | "">("");
  const [industry, setIndustry] = useState<string>("ecommerce");
  const [adType, setAdType] = useState<AdType>("displayAds");
  const [result, setResult] = useState<CTRResult | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!result) {
      return;
    }
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [result]);
  const calculateCTR = (clicks: number, impressions: number): number => {
    if (impressions === 0) return 0;
    return (clicks / impressions) * 100;
  };

  const getCtrStatus = (
    ctr: number,
    industry: string,
    adType: AdType
  ): "Poor" | "Good" | "Excellent" => {
    const benchmark = CTR_INDUSTRY_DATA[industry].benchmarks[adType];
    if (ctr < benchmark.min) return "Poor";
    if (ctr > benchmark.max) return "Excellent";
    return "Good";
  };
  const getTopRecommendations = (arr: string[], count = 3) => {
    return arr.slice(0, count);
  };
  const getRecommendations = (
  status: "Poor" | "Good" | "Excellent",
  industry: string,
  adType: AdType
): string[] => {
  const source = CTR_INDUSTRY_DATA[industry].recommendations[adType];

  if (status === "Poor") {
    return [
      source[0],
      source[1],
      source[2],
      "Urgently review and optimize your ad targeting",
      "Analyze and improve your ad copy and creative elements",
      "Consider A/B testing different variations",
    ];
  }

  if (status === "Good") {
    return [
      source[3],
      source[4],
      source[5],
      "Continue monitoring performance for optimization opportunities",
      "Test small variations to improve CTR further",
      "Focus on maintaining consistent performance",
    ];
  }

  return [
    source[6],
    source[7],
    source[8],
    "Document your successful strategies for future campaigns",
    "Consider scaling your current approach",
    "Look for opportunities to maintain high performance",
  ];
};

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setResult(null);

    const impressionsValue = Number(impressions);
    const clicksValue = Number(clicks);

    if (!impressionsValue || clicksValue < 0 || !industry || !adType) {
      setError("Please fill in all fields with valid values");
      return;
    }

    const ctr = calculateCTR(clicksValue, impressionsValue);
    const status = getCtrStatus(ctr, industry, adType);
    const benchmark = CTR_INDUSTRY_DATA[industry].benchmarks[adType];
    const industryName = CTR_INDUSTRY_DATA[industry].name;
    let potentialCTR: string;
    let improvement: string;
    let improvementClass: "ctr-positive" | "ctr-neutral";

    if (status === "Excellent") {
      potentialCTR = "Maintaining Excellence";
      improvement = "Keep up the great work!";
      improvementClass = "ctr-positive";
    } else {
      const targetCTR = status === "Good" ? benchmark.max : benchmark.min;
      const potentialClicks = Math.round((targetCTR / 100) * impressionsValue);
      const additionalClicks = potentialClicks - clicksValue;

      potentialCTR = `${targetCTR.toFixed(2)}% (${potentialClicks} clicks)`;

      if (additionalClicks > 0) {
        const improvementPercentage = ((targetCTR - ctr) / ctr) * 100;
        improvement = `+${additionalClicks} clicks (${improvementPercentage.toFixed(
          1
        )}% increase)`;
        improvementClass = "ctr-positive";
      } else {
        improvement = status === "Poor" ? "Needs improvement" : "On track";
        improvementClass = "ctr-neutral";
      }
    }

    const recommendations = getRecommendations(status, industry, adType);

    setResult({
      ctr,
      status,
      benchmarkMin: benchmark.min,
      benchmarkMax: benchmark.max,
      industryName,
      potentialCTR,
      improvement,
      improvementClass,
      recommendations,
    });
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const handleReset = () => {
    setImpressions("");
    setClicks("");
    setIndustry("ecommerce");
    setAdType("displayAds");
    setResult(null);
    setError("");
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <Box
        className={`${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"}`}
      >
        <div className="ctr-results">
          <h2 className="ctr-fs-4 text-center">Results</h2>
          <div className="ctr-results-grid">
            <Card className="ctr-result-card">
              <Card.Body className="text-center">
                <div className="ctr-result-header">
                  <strong>Your CTR</strong>
                </div>
                <div className="ctr-result-value">
                  {result.ctr.toFixed(2)}% ({clicks} clicks)
                </div>
                <div
                  className={`ctr-result-status ctr-status-${result.status.toLowerCase()}`}
                >
                  {result.status}
                </div>
              </Card.Body>
            </Card>

            <Card className="ctr-result-card">
              <Card.Body className="text-center">
                <div className="ctr-result-header">
                  <strong>Industry Benchmark</strong>
                </div>
                <div className="ctr-result-benchmark">
                  {result.benchmarkMin}% - {result.benchmarkMax}%
                </div>
                <div className="ctr-result-industry">{result.industryName}</div>
              </Card.Body>
            </Card>

            <Card className="ctr-result-card">
              <Card.Body className="text-center">
                <div className="ctr-result-header">
                  <strong>Potential CTR</strong>
                </div>
                <div className="ctr-result-potential">
                  {result.potentialCTR}
                </div>
                <div
                  className={`ctr-result-improvement ${result.improvementClass}`}
                >
                  {result.improvement}
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="ctr-recommendations">
            <h3 className="ctr-fs-5">
              <FaLightbulb className="me-2" /> Recommendations
            </h3>
            <ul>
              {result.recommendations.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
      </Box>
    );
  };

  return (
    <Box
      className={`${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"}`}
    >
      <Container
        className={`ctr-container ${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
          }`}
      >
        <Card className="ctr-calculator-card">
          <Card.Body>
            {!isInIframe && (
              <ToolsHeader
                title="CTR Calculator"
                description="Calculate Click-Through Rate (CTR) accurately to evaluate and
                boost your campaign success."
              />
            )}

            {error && (
              <div className="ctr-error-message p-3 mb-3 rounded text-danger">
                {error}
              </div>
            )}

            <Form onSubmit={handleSubmit} className="ctr-form-grid">
              <Form.Group className="ctr-form-group">
                <Form.Label>Impressions</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={impressions}
                  onChange={(e) =>
                    setImpressions(e.target.value ? Number(e.target.value) : "")
                  }
                  required
                  placeholder="Enter number of impressions"
                  className="ctr-custom-input"
                />
              </Form.Group>

              <Form.Group className="ctr-form-group">
                <Form.Label>Clicks</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={clicks}
                  onChange={(e) =>
                    setClicks(e.target.value ? Number(e.target.value) : "")
                  }
                  required
                  placeholder="Enter number of clicks"
                  className="ctr-custom-input"
                />
              </Form.Group>

              <Form.Group className="ctr-form-group">
                <Form.Label>Industry</Form.Label>
                <Form.Select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  required
                  className="ctr-custom-select"
                >
                  <option value="ecommerce">E-commerce</option>
                  <option value="technology">Technology</option>
                  <option value="finance">Finance</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="ctr-form-group">
                <Form.Label>Ad Type</Form.Label>
                <Form.Select
                  value={adType}
                  onChange={(e) => setAdType(e.target.value as AdType)}
                  required
                  className="ctr-custom-select"
                >
                  <option value="displayAds">Display Ads</option>
                  <option value="searchAds">Search Ads</option>
                  <option value="socialAds">Social Media Ads</option>
                  <option value="videoAds">Video Ads</option>
                  <option value="emailAds">Email Marketing</option>
                </Form.Select>
              </Form.Group>

              <div className="ctr-button-container">
                <div className="ctr-button-group d-flex justify-content-end gap-3">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="ctr-btn ctr-btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button type="submit" className="ctr-btn ctr-btn-primary">
                    Calculate CTR
                  </Button>
                </div>
              </div>
            </Form>

            {renderResult()}
          </Card.Body>
        </Card>
      </Container>
    </Box>
  );
};

export default CtrCalculator;
